import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { DataGrid, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import {
  MenuLoopBasic,
  MenuLoopSearchingParams,
  PaginatedSearchResultsMenuLoopBasic,
} from "client/jspPlatformExperiment";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";
import { useNavigate, useSearchParams } from "react-router-dom";
import Routes from "models/routes";
import DataGridListBox from "components/common/ui/DataGridListBox";
import MenuLoopListTableToolbar from "components/menu/ui/menuLoopList/menuLoopListTableToolbar";

interface MenuLoopListTableProps {}

const MenuLoopListTable: FC<MenuLoopListTableProps> = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<MenuLoopBasic[]>([]);
  const [rowCount, setRowCount] = useState(0);

  // Parse pagination from URL params
  const paginationModel = useMemo(() => {
    const page = parseInt(searchParams.get("page") || "0", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    return { page, pageSize };
  }, [searchParams]);

  /**
   * Handle pagination model change
   */
  const setPaginationModel = useCallback(
    (newModel: { page: number; pageSize: number }): void => {
      const newParams = new URLSearchParams(searchParams);
      const oldPageSize = paginationModel.pageSize;
      const oldPage = paginationModel.page;

      // Check if pageSize has changed
      if (newModel.pageSize !== oldPageSize) {
        // Calculate the first item index on the old page
        const firstItemIndex = oldPage * oldPageSize;
        // Calculate the equivalent page number for the new pageSize
        const equivalentPage = Math.floor(firstItemIndex / newModel.pageSize);
        newParams.set("page", equivalentPage.toString());
      } else {
        // If pageSize hasn't changed, just use the new page number
        newParams.set("page", newModel.page.toString());
      }

      newParams.set("pageSize", newModel.pageSize.toString());
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams, paginationModel],
  );

  // Parse searching params from URL params
  const searchingParams = useMemo<MenuLoopSearchingParams>(() => {
    const keywords = searchParams.get("keywords") || undefined;
    const sitesParam = searchParams.get("sites");
    const linkedUserGroupIdsAny =
      sitesParam && sitesParam.length > 0
        ? sitesParam
            .split(",")
            .map((id) => parseInt(id, 10))
            .filter((id) => !Number.isNaN(id))
        : undefined;

    return {
      keywords: keywords || undefined,
      linked_user_group_ids_any: linkedUserGroupIdsAny || undefined,
    };
  }, [searchParams]);

  /**
   * Handle searching params change
   */
  const setSearchingParams = useCallback(
    (newParams: MenuLoopSearchingParams): void => {
      const urlParams = new URLSearchParams(searchParams);

      if (newParams.keywords) {
        urlParams.set("keywords", newParams.keywords);
      } else {
        urlParams.delete("keywords");
      }

      if (
        newParams.linked_user_group_ids_any &&
        newParams.linked_user_group_ids_any.length > 0
      ) {
        urlParams.set("sites", newParams.linked_user_group_ids_any.join(","));
      } else {
        urlParams.delete("sites");
      }

      // Reset page to 0 when search params change
      urlParams.set("page", "0");

      setSearchParams(urlParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  /**
   * Initialize default URL params if not present
   */
  useEffect(() => {
    const hasPage = searchParams.has("page");
    const hasPageSize = searchParams.has("pageSize");

    if (!hasPage || !hasPageSize) {
      const newParams = new URLSearchParams(searchParams);
      if (!hasPage) {
        newParams.set("page", "0");
      }
      if (!hasPageSize) {
        newParams.set("pageSize", "10");
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  /**
   * Get the first batch of menu loop basic objects
   */
  useEffect(() => {
    jspPlatformApi()
      .menuLoop.postMenuLoopSearch({
        body: searchingParams,
        query: {
          number_per_page: paginationModel.pageSize,
          page_index: paginationModel.page,
          return_keys_only: true,
        },
      })
      .then((res) => {
        const data = res.data as PaginatedSearchResultsMenuLoopBasic;
        setRows(data?.data ?? []);
        setRowCount(data?.total_counts ?? -1);
      })
      .catch((reason) => {
        if (
          isApiError(reason) &&
          isErrorResponseBodyType(toJspApiError(reason).body)
        ) {
          // console.log(toJspApiError(reason).body.detail);
        }
      });
  }, [searchingParams, paginationModel.page, paginationModel.pageSize]);

  /**
   * Go to menu loop detail edit page
   * @param menuLoopId The menu loop's ID
   */
  const handleEditClick = (menuLoopId: number) => () => {
    navigate(`${Routes.MENU_LOOP_EDIT}/${menuLoopId}`, {
      replace: false,
      state: { editingMenuLoopId: menuLoopId },
    });
  };

  const columns: GridColDef<MenuLoopBasic>[] = [
    {
      field: "editAction",
      type: "actions",
      width: 45,
      cellClassName: "actions",
      getActions: ({ id, row }) => {
        const to = `${Routes.MENU_LOOP_EDIT}/${row.id}`;
        return [
          <GridActionsCellItem
            key={id}
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={(e) => {
              // Handle Ctrl/Cmd + click to open in new tab
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                window.open(to, "_blank");
              } else {
                handleEditClick(row.id)();
              }
            }}
            onMouseDown={(e) => {
              // Handle middle-click (wheel click) to open in new tab
              if (e.button === 1) {
                e.preventDefault();
                window.open(to, "_blank");
              }
            }}
            color="inherit"
          />,
        ];
      },
    },
    { field: "id", headerName: "ID", width: 90, editable: false },
    { field: "name", headerName: "Loop Name", flex: 180, editable: false },
    {
      field: "start_date",
      headerName: "Start",
      width: 100,
      editable: false,
    },
    { field: "end_date", headerName: "End", width: 100, editable: false },
  ];

  return (
    <DataGridListBox>
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        slots={{
          toolbar: MenuLoopListTableToolbar,
        }}
        slotProps={{
          toolbar: {
            setSearchingParams,
          },
        }}
        pageSizeOptions={[5, 10, 25, 50]}
        rowCount={rowCount}
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
      />
    </DataGridListBox>
  );
};

export default MenuLoopListTable;
